import { THEME } from "@myloc/myloc-gui";
import PropType from "prop-types";
import { useEffect, useState } from "react";
import { useTranslate } from "../../../../language/i18n";

import resourceGroupService from "../../../../services/resourceGroupService";
import { getPlace } from "../../../../utils/getPlace";
import AnnulFittingMeetingModal from "../../Modals/AnnulFittingMeetingModal";

import { useSelector } from "react-redux";
import { Car, Clock, Place, Room } from "../../../../assets/icons";
import pages from "../../../../utils/pages";
import CheckAuthButton from "../../../shared/Buttons/CheckAuthButton";
import styles from "../../FittingDetailPage.module.scss";
import CreateOrEditNextMeeting from "./CreateOrEditMeeting";

const NextMeeting = ({ fitting, setBreadcrumbs, embeddedFitting, setFetchFittings }) => {
  const translate = useTranslate();
  const authorizationList = useSelector(state => state.session.authorizationList);
  const address = fitting?.nextMeeting?.address
    ? [
        fitting?.nextMeeting?.address?.street,
        `${fitting?.nextMeeting?.address?.postalNo} ${fitting?.nextMeeting?.address?.city}`,
      ]
    : [];

  const [modal, setModal] = useState("");
  const [create, setCreate] = useState(false);
  const [edit, setEdit] = useState(false);
  const [options, setOptions] = useState({});
  const [meeting, setMeeting] = useState();
  const isCompleted = fitting.status.value === 90 || fitting.status.value === 99;

  useEffect(() => {
    setBreadcrumbs([
      {
        text: embeddedFitting?.text
          ? translate(embeddedFitting?.text)
          : `${translate(pages.MY_MYLOC.NAME)} - ${translate(pages.FITTINGS.NAME)}`,
        link: embeddedFitting?.path ? embeddedFitting?.path : pages.MY_FITTINGS.PATH,
      },
      {
        text: `${translate(pages.FITTING_DETAIL.NAME)} - ${translate(pages.MY_FITTINGS_NEXT_MEETING.NAME)}`,
      },
    ]);
  }, [embeddedFitting, setBreadcrumbs, translate]);

  useEffect(() => {
    if (edit || create) {
      Promise.all([
        resourceGroupService.getResourceGroup(fitting?.fittingType?.roomResourceGroup?.id, { subResource: true }),
        resourceGroupService.getResourceGroup(fitting?.fittingType?.carResourceGroup?.id, { subResource: true }),
        resourceGroupService.getResourceGroup(fitting?.fittingType?.consultantResourceGroup?.id, {
          mainResource: true,
        }),
        resourceGroupService.getResourceGroup(fitting?.fittingType?.technicianResourceGroup?.id, {
          mainResource: true,
        }),
      ]).then(([room, car, consultant, technician]) => {
        setOptions({
          room: room?.data?.page,
          car: car?.data?.page,
          consultant: consultant?.data?.page,
          technician: technician?.data?.page,
        });
      });

      setMeeting({
        consultantId: fitting.nextMeeting?.consultant?.id,
        technicianId: fitting.nextMeeting?.technician?.id,
        roomId: fitting.nextMeeting?.room?.id,
        carId: fitting.nextMeeting?.car?.id,
        date: fitting.nextMeeting?.date || "",
        fromTime: fitting.nextMeeting?.fromTime || "",
        toTime: fitting.nextMeeting?.toTime || "",
        id: fitting.nextMeeting?.id,
        fittingId: fitting.id,
        address: fitting.nextMeeting?.address || fitting.client?.defaultAddress,
        addressType: fitting.nextMeeting?.addressType?.id || "CLIENT",
      });
    }
  }, [fitting, edit, create]);

  const isAuthorized = authId => {
    return authorizationList?.find(item => item.type === "FUNCTION" && item.id === authId);
  };

  const NewMeeting = () => {
    return create ? (
      <CreateOrEditNextMeeting
        fitting={fitting}
        setMeeting={setMeeting}
        meeting={meeting}
        create={create}
        setCreate={setCreate}
        options={options}
        setFetchFittings={setFetchFittings}
      />
    ) : (
      <>
        <p>{translate("NO_MEETINGS_BOOKED")}</p>
        {!isCompleted && (
          <div className={styles.buttons}>
            <CheckAuthButton
              theme={THEME.SECONDARY}
              onClick={() => setCreate(true)}
              title={translate("NEW_MEETING")}
              authId={"FSP_CreateWebOrderFittingMeeting"}
            />
          </div>
        )}
      </>
    );
  };

  const NextMeetingDetails = () => {
    if (
      !fitting?.nextMeeting ||
      (fitting?.nextMeeting?.status == "BOOKED" && !isAuthorized("FSP_UpdateUnplannedWebOrderFittingMeetings"))
    ) {
      return <NewMeeting />;
    }

    const nextMeeting = fitting.nextMeeting;
    return (
      <>
        <div className={styles.meetingCard}>
          <div className={styles.meetingHeader}>
            <div className={styles.meetingTime}>
              <Clock customCssClass={styles.icon} />
              {`${nextMeeting.date || ""} ${nextMeeting.fromTime || ""} – ${nextMeeting.toTime || ""}`}
            </div>
          </div>

          <div className={styles.meetingBody}>
            <div>
              <div className={styles.meetingInfo}>
                <Place customCssClass={styles.icon} />
                {address.join(", ")}
              </div>
              <div className={styles.meetingInfo}>
                <span className={styles.participantLabel}>{translate("PLACE")}</span>
                {getPlace(nextMeeting, translate("CLIENT"))}
              </div>
              {nextMeeting.room?.label && (
                <div className={styles.meetingInfo}>
                  <Room customCssClass={styles.icon} />
                  {nextMeeting.room.label}
                </div>
              )}
              {nextMeeting.car?.label && (
                <div className={styles.meetingInfo}>
                  <Car customCssClass={styles.icon} />
                  {nextMeeting.car.label}
                </div>
              )}
            </div>

            <div className={styles.participantsSection}>
              {nextMeeting.consultant?.firstName && (
                <div className={styles.participant}>
                  <span className={styles.participantLabel}>{translate("CONSULTANT")}</span>
                  <div>
                    <div>{`${nextMeeting.consultant.firstName ?? ""} ${nextMeeting.consultant.surname ?? ""}`}</div>
                    {nextMeeting.consultant.phoneNo && <div>{nextMeeting.consultant.phoneNo}</div>}
                    {nextMeeting.consultant.email && <div>{nextMeeting.consultant.email}</div>}
                  </div>
                </div>
              )}
              {nextMeeting.technician?.firstName && (
                <div className={styles.participant}>
                  <span className={styles.participantLabel}>{translate("TECHNICIAN")}</span>
                  <div>
                    <div>{`${nextMeeting.technician.firstName ?? ""} ${nextMeeting.technician.surname ?? ""}`}</div>
                    {nextMeeting.technician.phoneNo && <div>{nextMeeting.technician.phoneNo}</div>}
                    {nextMeeting.technician.email && <div>{nextMeeting.technician.email}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {!isCompleted && (
            <div className={styles.meetingFooter}>
              <CheckAuthButton
                theme={THEME.SECONDARY}
                onClick={() => setEdit(true)}
                title={translate("EDIT_MEETING")}
                authId={
                  nextMeeting.status === "PLANNED"
                    ? "FSP_UpdatePlannedWebOrderFittingMeeting"
                    : "FSP_UpdateUnplannedWebOrderFittingMeeting"
                }
              />
              {nextMeeting.status === "PLANNED" && (
                <CheckAuthButton
                  theme={THEME.SECONDARY}
                  onClick={() => setModal("ANNUL_MEETING")}
                  title={translate("ANNUL_FITTING_MEETING")}
                  authId={"FSP_CancelWebOrderFittingMeeting"}
                />
              )}
            </div>
          )}
        </div>

        <AnnulFittingMeetingModal
          visible={modal === "ANNUL_MEETING"}
          fittingId={fitting.id}
          meetingId={nextMeeting.id}
          onClose={() => setModal("")}
          setFetchFittings={setFetchFittings}
        />
      </>
    );
  };

  const getTitle = () => {
    if (edit) {
      return translate("EDIT_MEETING");
    } else if (create) {
      return translate("NEW_MEETING");
    } else {
      return translate("FITTING_SUB_NAV_NEXT_MEETING");
    }
  };
  return (
    <section className={styles.nextMeeting}>
      <h3 className={styles.title}>{getTitle()}</h3>
      {edit ? (
        <CreateOrEditNextMeeting
          fitting={fitting}
          setMeeting={setMeeting}
          meeting={meeting}
          edit={edit}
          setEdit={setEdit}
          options={options}
          setFetchFittings={setFetchFittings}
        />
      ) : (
        <NextMeetingDetails />
      )}
    </section>
  );
};

NextMeeting.propTypes = {
  fitting: PropType.object,
  setBreadcrumbs: PropType.func,
  embeddedFitting: PropType.object,
  setFetchFittings: PropType.func,
};

export default NextMeeting;
